import { ReactNode } from 'react';
import { ResolvedDeviceFallbackConfig } from '../../types/deviceFallbackTheme';
import { ICON_DROP_PX, iconDropPx } from './iconDropScale';
import { IconDropCopy } from './iconDropCopy';

/**
 * Sits below the copy colour rather than at an alpha of it. The canvas wants this to recede
 * almost into the fade, which a translucent lavender never does on the purple.
 */
const QUIET_ACTION_COLOR = '#4A3D6B';

/** Tightened to the canvas, as a fraction of the size so it holds when the type scales. */
const HEADLINE_TRACKING = '-0.011em';

/**
 * The measurements a screen is allowed to run differently from the ad, in canvas pixels.
 * Everything else about the block is fixed, so the screens cannot drift apart on the parts
 * the canvas draws identically.
 */
export interface IconDropCopyMetrics {
  headline: number;
  subcopy: number;
  cta: number;
  /** Air between the subcopy and the offer. */
  ctaGap: number;
  /** Side margins. The unavailable screen pulls its shorter lines in further. */
  copyInset: number;
}

const AD_COPY_METRICS: IconDropCopyMetrics = {
  headline: ICON_DROP_PX.headline,
  subcopy: ICON_DROP_PX.subcopy,
  cta: ICON_DROP_PX.cta,
  ctaGap: ICON_DROP_PX.ctaGap,
  copyInset: ICON_DROP_PX.copyInset,
};

interface IconDropCopyBlockProps {
  config: ResolvedDeviceFallbackConfig;
  copy: IconDropCopy;
  metrics?: Partial<IconDropCopyMetrics>;
  /** Sits above the headline, for the screens that lead with a mark or an eyebrow line. */
  leading?: ReactNode;
  /**
   * Replaces the string subcopy when a screen needs mixed-colour text. Rendered
   * at full opacity so caller-set colours read exactly as given.
   */
  subcopyNode?: ReactNode;
  onCta?: () => void;
  onQuietAction?: () => void;
  /**
   * Renders the offer as a real anchor rather than a button, so embedded WebViews
   * (AppLovin/LoopMe, etc.) can navigate — they block window.open. The click
   * handler still runs first, for analytics.
   */
  ctaHref?: string;
}

/**
 * An empty label means the screen makes no offer — a thank-you with no CTA URL —
 * so the gold plate is left off rather than rendered as a blank strip. Every
 * Icon Drop variant ships a label, so this only bites where a caller opts in.
 */
const hasOffer = (copy: IconDropCopy): boolean => copy.ctaLabel.trim().length > 0;

/**
 * Headline, subcopy and offer, anchored to the bottom of the stage.
 *
 * Every Icon Drop screen in the canvas ends this way, so the offer lands under the thumb
 * rather than in the middle of the screen. The quiet action is optional: the ad offers a
 * way past it, the confirmation has nothing left to decline.
 */
export const IconDropCopyBlock = ({ config, copy, metrics, leading, subcopyNode, onCta, onQuietAction, ctaHref }: IconDropCopyBlockProps) => {
  const { headline, subcopy, cta, ctaGap, copyInset } = { ...AD_COPY_METRICS, ...metrics };
  const ctaStyle = {
    marginTop: iconDropPx(ctaGap),
    height: iconDropPx(ICON_DROP_PX.ctaHeight),
    borderRadius: iconDropPx(ICON_DROP_PX.ctaRadius),
    backgroundColor: config.qrRimStartColor,
    color: config.stageColor,
    fontSize: iconDropPx(cta),
    lineHeight: 1,
    boxSizing: 'border-box' as const,
    border: 'none',
    paddingBlock: 0,
    paddingInline: iconDropPx(ICON_DROP_PX.ctaPaddingX),
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    cursor: onCta || ctaHref ? 'pointer' : 'default',
  };
  const quietStyle = {
    marginTop: iconDropPx(ICON_DROP_PX.quietActionGap),
    padding: iconDropPx(ICON_DROP_PX.quietActionPadding),
    color: QUIET_ACTION_COLOR,
    fontSize: iconDropPx(ICON_DROP_PX.quietAction),
    background: 'transparent',
    border: 'none',
    appearance: 'none' as const,
    WebkitAppearance: 'none' as const,
    cursor: onQuietAction ? 'pointer' : 'default',
  };

  return (
    <div
      className='absolute inset-x-0 z-10 flex flex-col items-center'
      style={{
        bottom: `max(8%, ${iconDropPx(ICON_DROP_PX.copyBlockFloor)})`,
        paddingInline: iconDropPx(copyInset),
      }}
    >
      {leading}

      <h1
        className='whitespace-nowrap text-center font-bold'
        style={{
          color: config.qrRimStartColor,
          fontSize: iconDropPx(headline),
          lineHeight: 1.2,
          letterSpacing: HEADLINE_TRACKING,
        }}
      >
        {copy.headline}
      </h1>

      {subcopyNode ?? (
        <p
          className='whitespace-nowrap text-center'
          style={{
            marginTop: iconDropPx(ICON_DROP_PX.headlineGap),
            color: config.copyColor,
            fontSize: iconDropPx(subcopy),
            lineHeight: 1.45,
            opacity: 0.9,
          }}
        >
          {copy.subcopy}
        </p>
      )}

      {hasOffer(copy) &&
        (ctaHref && onCta ? (
          <a
            href={ctaHref}
            target='_self'
            rel='noopener noreferrer'
            className='flex w-full items-center justify-center text-center font-bold no-underline'
            style={ctaStyle}
            onClick={onCta}
          >
            {copy.ctaLabel}
          </a>
        ) : onCta ? (
          <button
            type='button'
            className='flex w-full items-center justify-center text-center font-bold'
            style={ctaStyle}
            onClick={onCta}
          >
            {copy.ctaLabel}
          </button>
        ) : (
          <div className='flex w-full items-center justify-center text-center font-bold' style={ctaStyle}>
            {copy.ctaLabel}
          </div>
        ))}

      {copy.quietActionLabel ? (
        onQuietAction ? (
          <button type='button' className='font-semibold' style={quietStyle} onClick={onQuietAction}>
            {copy.quietActionLabel}
          </button>
        ) : (
          <span className='font-semibold' style={quietStyle}>
            {copy.quietActionLabel}
          </span>
        )
      ) : null}
    </div>
  );
};
